import React, { useState } from 'react';
import Modal from 'react-modal';

import { TimesheetFooter } from './TimesheetFooter';
import { TimesheetHeader } from './TimesheetHeader';
import { TimesheetOverview } from './TimesheetOverview';
import { TimesheetDescription } from './TimesheetDescription';
import { TimesheetPayRates } from './TimesheetPayRates';
import { TimesheetRejections } from './TimesheetRejections';
import { TExtraRateOption } from './types';
import { useTimesheetDetails } from './useTimesheetDetails';
import { TimesheetResendEmailModal } from './TimesheetResendEmailModal';

type TimesheetDetailsModalProps = {
    isOpen: boolean;
    timesheetId?: number | null;
    onClose: () => void;
    notification?: { success: (title: string, message: string) => void; error?: (title: string, message: string) => void };
    onSaved?: () => void;
    allowEdit: boolean;
    rateOptions: TExtraRateOption[];
};

const modalStyles = {
    content: {
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '45vw',
        minWidth: '640px',
        maxWidth: '900px',
        maxHeight: '85vh',
        padding: 0,
        border: 'none',
        borderRadius: '16px',
        overflow: 'hidden',
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
    },
};

export const TimesheetDetailsModal: React.FC<TimesheetDetailsModalProps> = ({
    isOpen,
    timesheetId,
    onClose,
    notification,
    onSaved,
    allowEdit,
    rateOptions,
}) => {
    const [isResendModalOpen, setIsResendModalOpen] = useState(false);
    const {
        loading,
        details,
        formState,
        baseRates,
        additionalRates,
        lastThreeRejections,
        canEdit,
        canResendApprovalEmail,
        effectiveIsEditing,
        isGenerating,
        isResendingApprovalEmail,
        lastApprovalEmailSentAt,
        handleDescriptionChange,
        handleUpdateRate,
        handleAddAdditionalRate,
        handleRemoveRate,
        handleToggleEditGuarded,
        handleSave,
        handleResendApprovalEmail,
    } = useTimesheetDetails({
        isOpen,
        timesheetId,
        allowEdit,
        notification,
        onSaved,
        onClose,
    });

    const freelancerName = details?.freelancer_name || '-';
    const clientName = details?.client_name || '-';
    const agentName = details?.agent_name || '-';
    const agentCode = details?.consultant_code || '-';
    const timesheetDate = details?.timesheet_date || '-';
    const personnelReference = details?.personnel_reference || '-';
    const clientReference = details?.client_reference || '-';
    const employerReference = details?.employer_reference || '-';
    const division = details?.division_reference || '-';
    const department = details?.department_reference || '-';

    return (
        <Modal
            className="timesheet-modal"
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Update Timesheet"
            style={modalStyles}
            ariaHideApp={false}
        >
            <div className="timesheet-modal__container">
                <TimesheetHeader
                    title={
                        details?.timesheet_number
                            ? `Timesheet #${details.timesheet_number}`
                            : 'Timesheet Details'
                    }
                    onClose={onClose}
                />
                <div className="timesheet-modal__body">
                    {loading && <div>Loading…</div>}
                    {!loading && details && (
                        <>
                            <TimesheetRejections
                                statusDisplay={details.status_display}
                                rejections={lastThreeRejections}
                            />

                            <div className="timesheet-modal-table-wrapper">
                                <div className="timesheet-group">
                                    <div className="timesheet-group-header">
                                        <TimesheetOverview
                                            freelancerName={freelancerName}
                                            clientName={clientName}
                                            agentName={agentName}
                                            agentConsultantCode={agentCode}
                                            timesheetDate={timesheetDate}
                                            personnelReference={personnelReference}
                                            employerReference={employerReference}
                                            clientReference={clientReference}
                                            division={division}
                                            department={department}
                                        />
                                        <TimesheetDescription
                                            description={
                                                formState.job_description ||
                                                ''
                                            }
                                            isEditing={effectiveIsEditing}
                                            inputId="timesheet-description-readonly"
                                            onChange={handleDescriptionChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <TimesheetPayRates
                                title="Pay Rates"
                                rates={baseRates}
                                isEditing={effectiveIsEditing}
                                canEditCode={false}
                                rateOptions={rateOptions}
                                emptyText="No pay rates"
                                onUpdateRate={handleUpdateRate}
                            />

                            <TimesheetPayRates
                                title="Additional Rates"
                                rates={additionalRates}
                                isEditing={effectiveIsEditing}
                                canEditCode={effectiveIsEditing}
                                rateOptions={rateOptions}
                                emptyText="No additional rates"
                                onUpdateRate={handleUpdateRate}
                                onRemoveRate={handleRemoveRate}
                            />
                        </>
                    )}
                </div>
                <TimesheetFooter
                    isEditing={effectiveIsEditing}
                    isGenerateDisabled={!canEdit || isGenerating}
                    isGenerating={isGenerating}
                    isResending={isResendingApprovalEmail}
                    isResendDisabled={!canResendApprovalEmail}
                    disabledTooltip={
                        canEdit ? undefined : 'Editing available only for rejected timesheets'
                    }
                    onAddExtraRate={effectiveIsEditing ? handleAddAdditionalRate : undefined}
                    onResendEmail={() => setIsResendModalOpen(true)}
                    onToggleEdit={handleToggleEditGuarded}
                    onGenerate={handleSave}
                    onCancel={onClose}
                    editDisabled={!canEdit}
                />
                <TimesheetResendEmailModal
                    isOpen={isResendModalOpen}
                    lastApprovalEmailSentAt={lastApprovalEmailSentAt}
                    isSending={isResendingApprovalEmail}
                    onClose={() => setIsResendModalOpen(false)}
                    onSend={() => {
                        handleResendApprovalEmail().then(success => {
                            if (success) {
                                setIsResendModalOpen(false);
                            }
                        });
                    }}
                />
            </div>
        </Modal>
    );
};

export default TimesheetDetailsModal;
